import React, { useState, useEffect } from 'react';
import { signOut } from "firebase/auth";
import { auth, db } from '../../firebase';
import './Sidebar.css';
import {
  Home as HomeIcon,
  QrCodeScanner as QrCodeScannerIcon,
  ChevronLeft,
  Add,
  Logout as LogoutIcon,
  InventoryOutlined as InventoryOutlineIcon,
  PaidOutlined as PaidIcon,
  Menu as MenuIcon,
  LocalShippingOutlined as DriverSummaryIcon,
  AssignmentOutlined as PaymentRecordIcon,
  ManageAccountsOutlined as UACIcon,
  DashboardOutlined as Dashboard,
  AssignmentReturnOutlined as ReturnsIcon,
  FactCheckOutlined as ReconciliationIcon
} from '@mui/icons-material';

import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { doc, getDoc } from 'firebase/firestore';
import { rolesOf, isOwner, isAdminOrOwner, canReconcile, roleLabel } from '../../roles';

const Sidebar = () => {
  const [open, setOpen] = useState(false); // Start closed on mobile/tablet
  const [user] = useAuthState(auth);
  // The full role list, not a single role: a profile can hold several, and
  // gating on roles[0] hid every admin/owner tool from anyone whose first
  // listed role was something else.
  const [roles, setRoles] = useState([]);
  const location = useLocation();

  const showAdminTools = isAdminOrOwner(roles);
  const showOwnerTools = isOwner(roles);
  const showReconciliation = canReconcile(roles);
  
  // Close sidebar when clicking outside on mobile/tablet
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (open && window.innerWidth < 1024) {
        const sidebar = document.querySelector('.sidebar-container');
        const hamburger = document.querySelector('.mobile-hamburger');
        if (sidebar && !sidebar.contains(e.target) && hamburger && !hamburger.contains(e.target)) {
          setOpen(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Handle navigation and close sidebar on mobile/tablet
  const handleNavigate = (path) => {
    navigate(path);
    if (window.innerWidth < 1024) {
      setOpen(false);
    }
  };

  useEffect(() => {
    const fetchUserRoles = async () => {
      if (!user) {
        setRoles([]); // Drop elevated nav the moment someone signs out
        return;
      }
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        setRoles(userDoc.exists() ? rolesOf(userDoc.data()) : []);
      } catch (error) {
        console.error('Error loading user roles:', error);
        setRoles([]);
      }
    };
    fetchUserRoles();
  }, [user]);

  const navigate = useNavigate();
  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/signin');
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const displayName = user?.displayName || user?.email || "Account";

  return (
    <>
      {/* Mobile Hamburger Button - Hidden when sidebar is open */}
      {!open && (
        <button
          className="mobile-hamburger fixed top-4 left-4 z-[60] lg:hidden bg-white rounded-lg shadow-lg p-3 hover:bg-gray-50 active:bg-gray-100 transition-all duration-200"
          aria-label="Open menu"
          onClick={() => setOpen(true)}
        >
          <MenuIcon className="text-[#1e293b]" fontSize="medium" />
        </button>
      )}

      {/* Overlay for mobile */}
      {open && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[45] lg:hidden transition-opacity duration-300"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Sidebar Container */}
      <div 
        className={`sidebar-container fixed top-0 left-0 h-screen flex flex-col bg-[#1e293b] text-white shadow-2xl z-[50] transition-all duration-300 ease-in-out
          ${open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          w-64 md:w-72 lg:w-72`}
      >
        {/* Mobile/Tablet Header */}
        <div className="lg:hidden flex items-center justify-between p-4 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-lg">
                {displayName.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <div className="text-sm font-semibold text-white truncate max-w-[150px] md:max-w-[200px]">
                {displayName}
              </div>
              <div className="text-xs text-slate-400">
                {roleLabel(roles)}
              </div>
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
            aria-label="Close menu"
          >
            <ChevronLeft fontSize="medium" />
          </button>
        </div>

        {/* Desktop Header */}
        <div className="hidden lg:flex items-center justify-center p-6 border-b border-slate-700">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-2">
              <span className="text-white font-bold text-2xl">
                {displayName.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="text-sm font-semibold text-white">
              {displayName}
            </div>
            <div className="text-xs text-slate-400 mt-1">
              {roleLabel(roles)}
            </div>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          <div className="space-y-1">
            <NavItem 
              icon={<HomeIcon />}
              text="Home"
              onClick={() => handleNavigate('/')}
              isActive={location.pathname === '/'}
            />
            <NavItem 
              icon={<Add />}
              text="Add Package"
              onClick={() => handleNavigate('/add')}
              isActive={location.pathname === '/add'}
            />
            <NavItem 
              icon={<QrCodeScannerIcon />}
              text="Scan QR Code"
              onClick={() => handleNavigate('/scanner')}
              isActive={location.pathname === '/scanner'}
            />
            <NavItem
              icon={<DriverSummaryIcon />}
              text="Driver Summary"
              onClick={() => handleNavigate('/driverSummary')}
              isActive={location.pathname === '/driverSummary'}
            />

            {/* Admin tier: the pages App.js wraps in AdminOrOwnerRoute. Owners
                hold admin's access too, so this shows for both. */}
            {showAdminTools && (
              <>
                <div className="my-3 border-t border-slate-700"></div>
                <div className="px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Admin Tools
                </div>
                <NavItem
                  icon={<InventoryOutlineIcon />}
                  text="Summary"
                  onClick={() => handleNavigate('/summary')}
                  isActive={location.pathname === '/summary'}
                />
              </>
            )}

            {/* Owner tier: the pages App.js wraps in OwnerRoute. */}
            {showOwnerTools && (
              <>
                <div className="my-3 border-t border-slate-700"></div>
                <div className="px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Owner Tools
                </div>
                <NavItem
                  icon={<PaidIcon />}
                  text="Payment History"
                  onClick={() => handleNavigate('/paymentHistory')}
                  isActive={location.pathname === '/paymentHistory'}
                />
                <NavItem
                  icon={<Dashboard />}
                  text="Dashboard"
                  onClick={() => handleNavigate('/deliveryDashboard')}
                  isActive={location.pathname === '/deliveryDashboard'}
                />
                <NavItem
                  icon={<DriverSummaryIcon />}
                  text="User Package Summary"
                  onClick={() => handleNavigate('/userpackagesummary')}
                  isActive={location.pathname === '/userpackagesummary'}
                />
                <NavItem
                  icon={<UACIcon />}
                  text="User Admin"
                  onClick={() => handleNavigate('/useradmin')}
                  isActive={location.pathname === '/useradmin'}
                />
              </>
            )}

            {/* Reconciliation tier: the reconciliation role or an owner. One
                entry with one condition, matching RoleRoute's own `canReconcile`
                on the route — a reconciler holds no other elevated role, so this
                stands on its own rather than living inside Owner Tools. */}
            {showReconciliation && (
              <>
                <div className="my-3 border-t border-slate-700"></div>
                <div className="px-3 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Reconciliation
                </div>
                <NavItem
                  icon={<ReconciliationIcon />}
                  text="Payment Reconciliation"
                  onClick={() => handleNavigate('/reconciliation')}
                  isActive={location.pathname === '/reconciliation'}
                />
                <NavItem
                  icon={<PaymentRecordIcon />}
                  text="Payment Record"
                  onClick={() => handleNavigate('/paymentRecord')}
                  isActive={location.pathname === '/paymentRecord'}
                />
                <NavItem
                  icon={<ReturnsIcon />}
                  text="Returns"
                  onClick={() => handleNavigate('/returns')}
                  isActive={location.pathname === '/returns'}
                />
              </>
            )}
          </div>
        </nav>

        {/* Footer with Logout */}
        <div className="border-t border-slate-700 p-3">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-slate-300 hover:bg-red-600 hover:text-white rounded-lg transition-all duration-200 group"
          >
            <LogoutIcon className="group-hover:scale-110 transition-transform" />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </div>
    </>
  );
};

// NavItem Component for cleaner code
const NavItem = ({ icon, text, onClick, isActive }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 group
      ${isActive 
        ? 'bg-blue-600 text-white shadow-lg' 
        : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
      }`}
  >
    <span className="group-hover:scale-110 transition-transform">
      {icon}
    </span>
    <span className="font-medium text-sm">
      {text}
    </span>
  </button>
);

export default Sidebar;